import React, { useState, useEffect, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { MessageSquare, User, Activity, LogOut, ChevronLeft, ChevronRight, Calendar, CalendarDays, House as Home, Settings, Shield, MapPin, Ellipsis as MoreHorizontal, Users, ChevronDown, Eye, Dumbbell, ChartColumn as BarChart3, Coins, Bot } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { useCoaching } from '../context/CoachingContext';
import { useCoachingBadge } from '../context/V2Context';
import { cn } from '../lib/utils';
import { Wordmark } from './auth/AuthLayout';
import { Avatar } from './ds';
import AithloIcon from './icons/AithloIcon';

const Sidebar = () => {
  const location = useLocation();
  const { logout, user } = useAuth();
  const { athletes, viewingAthlete, switchToAthlete, switchToSelf } = useCoaching();
  // v2: /v2/me's coaching_unread + the stream's coaching.unread; v1: the hub (aithlo-frontend#67).
  const unreadCount = useCoachingBadge();
  // Collapsed is remembered and told to Layout (`sidebar:layout`), which offsets the page by it:
  // the page kept a 256px gutter behind a 64px rail (2026-10-27 review).
  const [isCollapsed, setIsCollapsed] = useState(() => {
    // Collapsed as chosen; never chosen, a tablet (under 1024px) starts on the rail.
    try {
      const saved = localStorage.getItem('aithlo:sidebarCollapsed');
      return saved === null ? window.innerWidth < 1024 : saved === '1';
    } catch { return false; }
  });
  useEffect(() => {
    try { localStorage.setItem('aithlo:sidebarCollapsed', isCollapsed ? '1' : '0'); } catch { /* this visit */ }
    window.dispatchEvent(new CustomEvent('sidebar:layout', { detail: { collapsed: isCollapsed } }));
  }, [isCollapsed]);
  const [isMobileOpen, setIsMobileOpen] = useState(false);
  const [showMoreMenu, setShowMoreMenu] = useState(false);
  const [showAthleteSwitcher, setShowAthleteSwitcher] = useState(false);
  const moreMenuRef = useRef(null);
  const switcherRef = useRef(null);

  const isAdmin = user?.tier === 'admin';

  // The redesign's navigation (Figma "Web · sidebar"): Today · Plan · Coach ·
  // Stats · Aithlo Shift, then Workouts (and Routes), then Admin. Same pages as
  // before under the design's names (Home is Today, Calendar is Plan). Coach
  // opens the docked coach panel beside the Coaching page (the notebook, human
  // coaches, the coaching unread badge); a coach viewing an athlete gets the
  // panel alone, Coaching being the viewer's own. You (Profile), Settings and
  // Admin (its own workspace) sit with the avatar at the bottom. Segments stays hidden.
  const menuItems = [
    { path: '/dashboard', icon: Home, label: 'Today', athleteData: true },
    { path: '/calendar', icon: CalendarDays, label: 'Plan', athleteData: true },
    { ...(viewingAthlete ? {} : { path: '/coaching' }), action: 'coach', icon: MessageSquare, label: 'Coach' },
    { path: '/stats', icon: BarChart3, label: 'Stats', athleteData: true },
    { path: '/activities', icon: Activity, label: 'Activities', athleteData: true },
    { path: '/shift', icon: AithloIcon, label: 'Aithlo Shift', athleteData: true },
    { path: '/workouts', icon: Dumbbell, label: 'Workouts', group: 'tools' },
    ...(isAdmin ? [
      { path: '/route-editor', icon: MapPin, label: 'Routes', group: 'tools' },
    ] : []),
  ];
  const openCoach = () => window.dispatchEvent(new CustomEvent('chat:open', { detail: { dock: true } }));
  const unreadBadge = unreadCount > 0 && (
    <span className="flex h-4 min-w-[16px] items-center justify-center rounded-full bg-accent-default px-1 text-[10px] font-bold text-accent-on">
      {unreadCount > 9 ? '9+' : unreadCount}
    </span>
  );

  // The coach console has sub-routes (/admin/coach/runs/…); the rest match exactly.
  const isActive = (path) => location.pathname === path.split('?')[0]
    || (path === '/admin/coach' && location.pathname.startsWith('/admin/coach/'));

  // Close mobile menu when route changes
  useEffect(() => {
    setIsMobileOpen(false);
  }, [location.pathname]);

  // Handle window resize
  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth > 768) {
        setIsMobileOpen(false);
      }
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Close More menu / athlete switcher on outside click
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (moreMenuRef.current && !moreMenuRef.current.contains(e.target)) {
        setShowMoreMenu(false);
      }
      if (switcherRef.current && !switcherRef.current.contains(e.target)) {
        setShowAthleteSwitcher(false);
      }
    };
    if (showMoreMenu || showAthleteSwitcher) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showMoreMenu, showAthleteSwitcher]);

  // Close More menu on route change
  useEffect(() => {
    setShowMoreMenu(false);
  }, [location.pathname]);

  // Phone browsers: the first four pages in the bottom bar (Coach among them, the
  // panel over the Coaching page), the rest (and You, Settings) under More.
  // A coach viewing an athlete keeps Coach in the bar as a button (the panel alone):
  // the floating button no longer shows on a phone, so the bar is the way in.
  const pages = menuItems.filter((i) => i.path || i.action === 'coach');
  const mobileMainItems = pages.slice(0, 4);
  const mobileMoreItems = [
    ...pages.slice(4),
    { path: '/profile', icon: User, label: 'You', athleteData: true, dividerBefore: true },
    { path: '/settings', icon: Settings, label: 'Settings' },
  ];

  return (
    <>
      {/* Desktop Sidebar */}
      <div
        className={cn(
          "fixed left-0 top-0 z-40 h-screen bg-bg-panel border-r border-border-subtle transition-all duration-300 hidden md:flex flex-col font-body",
          isCollapsed ? "w-16" : "w-[232px]"
        )}
      >
        {/* Logo */}
        <div className={cn("flex h-16 items-center px-5", isCollapsed && "justify-center px-0")}>
          {isCollapsed
            ? <AithloIcon size={22} className="text-accent-default" role="img" aria-label="aithlo" aria-hidden={false} />
            : <Wordmark />}
        </div>

        {/* Athlete Switcher (show if user has athletes or is viewing one) */}
        {(athletes.length > 0 || viewingAthlete) && (
          <div className="px-3 py-2 border-b border-border-subtle" ref={switcherRef}>
            <div className="relative">
              <button
                onClick={() => setShowAthleteSwitcher(!showAthleteSwitcher)}
                className={cn(
                  "w-full flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium transition-all",
                  viewingAthlete
                    ? "bg-accent-tint text-accent-default border border-accent-tint-border"
                    : "text-text-secondary hover:bg-bg-raised",
                  isCollapsed && "justify-center px-2"
                )}
                title={isCollapsed ? (viewingAthlete ? viewingAthlete.username : 'My Data') : ''}
              >
                <Eye className="w-4 h-4 flex-shrink-0" />
                {!isCollapsed && (
                  <>
                    <span className="truncate flex-1 text-left">
                      {viewingAthlete ? viewingAthlete.username : 'My Data'}
                    </span>
                    <ChevronDown className={cn("w-4 h-4 transition-transform", showAthleteSwitcher && "rotate-180")} />
                  </>
                )}
              </button>

              {showAthleteSwitcher && (
                <div className={cn(
                  "absolute z-50 mt-1 bg-bg-surface rounded-lg shadow-lg border border-border-default py-1",
                  isCollapsed ? "left-full ml-2 top-0 w-48" : "left-0 right-0"
                )}>
                  <button
                    onClick={() => { switchToSelf(); setShowAthleteSwitcher(false); }}
                    className={cn(
                      "w-full flex items-center gap-2 px-3 py-2 text-sm transition-colors",
                      !viewingAthlete ? "text-accent-default bg-accent-tint font-medium" : "text-text-secondary hover:bg-bg-raised"
                    )}
                  >
                    <User className="w-4 h-4" />
                    My Data
                  </button>
                  {athletes.map((a) => (
                    <button
                      key={a.id}
                      onClick={() => { switchToAthlete(a); setShowAthleteSwitcher(false); }}
                      className={cn(
                        "w-full flex items-center gap-2 px-3 py-2 text-sm transition-colors",
                        viewingAthlete?.id === a.id ? "text-accent-default bg-accent-tint font-medium" : "text-text-secondary hover:bg-bg-raised"
                      )}
                    >
                      <Activity className="w-4 h-4" />
                      {a.username}
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}

        {/* Navigation */}
        <nav className="flex-1 space-y-0.5 overflow-y-auto px-3 py-2" aria-label="Main">
          {menuItems.map((item, idx) => {
            const active = item.path && isActive(item.path);
            const groupStart = item.group && item.group !== menuItems[idx - 1]?.group;
            const cls = cn(
              "relative flex w-full items-center gap-3 rounded-xl px-3 py-2.5 type-label transition-colors",
              active
                ? "bg-bg-raised text-text-primary"
                : viewingAthlete && item.athleteData
                  ? "bg-accent-tint text-accent-default"
                  : "text-text-secondary hover:bg-bg-raised hover:text-text-primary",
              isCollapsed && "justify-center px-2"
            );
            const inner = (
              <>
                <item.icon className={cn("h-[18px] w-[18px] shrink-0", active && "text-accent-default")} aria-hidden="true" />
                {!isCollapsed && <span className="flex-1 text-left">{item.label}</span>}
                {item.action === 'coach' && (isCollapsed ? <span className="absolute right-1 top-1">{unreadBadge}</span> : unreadBadge)}
              </>
            );
            return (
              <React.Fragment key={item.path || item.action}>
                {/* Athlete data label — before the first athleteData item when viewing an athlete */}
                {viewingAthlete && item.athleteData && (idx === 0 || !menuItems[idx - 1].athleteData) && !isCollapsed && (
                  <div className="px-3 pb-1 pt-2 type-eyebrow text-accent-default">
                    {viewingAthlete.username}'s data
                  </div>
                )}
                {groupStart && <div className="mx-2 my-3 border-t border-border-subtle" />}
                {item.path
                  ? <Link to={item.path} onClick={item.action === 'coach' ? openCoach : undefined} className={cls} aria-current={active ? 'page' : undefined} title={isCollapsed ? item.label : undefined}>{inner}</Link>
                  : <button type="button" onClick={openCoach} className={cls} title={isCollapsed ? item.label : undefined}>{inner}</button>}
              </React.Fragment>
            );
          })}
        </nav>

        {/* You (Profile), Settings, Log out, collapse. */}
        <div className="space-y-1 p-3">
          <Link to="/profile"
            className={cn("flex items-center gap-3 rounded-2xl border border-border-subtle bg-bg-surface p-2.5 transition-colors hover:border-border-default",
              isActive('/profile') && "border-border-default", isCollapsed && "justify-center border-0 bg-transparent p-1")}
            title={isCollapsed ? 'You' : undefined}>
            <Avatar name={[user?.firstname, user?.lastname].filter(Boolean).join(' ') || user?.username || user?.email} src={user?.picture} size={36} />
            {!isCollapsed && (
              <span className="min-w-0 flex-1">
                <span className="block truncate type-label-section text-text-primary">{user?.firstname || user?.username || 'You'}</span>
                <span className="block truncate type-caption capitalize text-text-tertiary">{(user?.tier || '').replace(/_/g, ' ')}</span>
              </span>
            )}
          </Link>
          <div className={cn("flex items-center gap-1", isCollapsed ? "flex-col" : "px-1")}>
            {/* Admin is its own workspace (its own nav), reached from here. */}
            {isAdmin && (
              <Link to="/admin" title="Admin" aria-label="Admin"
                className={cn("flex h-9 w-9 items-center justify-center rounded-lg text-text-tertiary hover:bg-bg-raised hover:text-text-primary", location.pathname.startsWith('/admin') && "text-text-primary")}>
                <Shield className="h-[18px] w-[18px]" />
              </Link>
            )}
            <Link to="/settings" title="Settings" aria-label="Settings"
              className={cn("flex h-9 w-9 items-center justify-center rounded-lg text-text-tertiary hover:bg-bg-raised hover:text-text-primary", isActive('/settings') && "text-text-primary")}>
              <Settings className="h-[18px] w-[18px]" />
            </Link>
            <button type="button" onClick={logout} title="Log out" aria-label="Log out"
              className="flex h-9 w-9 items-center justify-center rounded-lg text-text-tertiary hover:bg-bg-raised hover:text-status-danger">
              <LogOut className="h-[18px] w-[18px]" />
            </button>
            <button type="button" onClick={() => setIsCollapsed(!isCollapsed)}
              title={isCollapsed ? 'Expand sidebar' : 'Collapse sidebar'} aria-label={isCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
              className={cn("flex h-9 w-9 items-center justify-center rounded-lg text-text-tertiary hover:bg-bg-raised hover:text-text-primary", !isCollapsed && "ml-auto")}>
              {isCollapsed ? <ChevronRight className="h-4 w-4" /> : <ChevronLeft className="h-4 w-4" />}
            </button>
          </div>
        </div>
      </div>

      {/* Mobile Bottom Navigation. Its padding keeps the icons clear of an iPhone's home
          indicator and, in landscape, its notch (--safe-*, index.css; viewport-fit=cover).
          The class it named for this (safe-area-bottom) was never defined, so the
          labels sat under the indicator. Its height is --mobile-nav-h: change both. */}
      <nav aria-label="Main pages" data-mobile-nav
        className="fixed bottom-0 left-0 right-0 z-50 bg-bg-surface border-t border-border-default md:hidden pb-(--safe-bottom) pl-(--safe-left) pr-(--safe-right)">
        <div className="flex items-center justify-around py-2 px-1">
          {mobileMainItems.map((item) => {
            const Tag = item.path ? Link : 'button';
            return (
            <Tag
              key={item.path || item.action}
              {...(item.path ? { to: item.path } : { type: 'button' })}
              onClick={item.action === 'coach' ? openCoach : undefined}
              className={cn(
                "relative flex flex-col items-center gap-1 px-2 py-1.5 rounded-lg min-w-[60px] transition-colors",
                item.path && isActive(item.path)
                  ? "text-accent-default"
                  : viewingAthlete && item.athleteData
                    ? "text-accent-default bg-accent-tint"
                    : "text-text-tertiary hover:text-text-body"
              )}
            >
              <item.icon className="w-5 h-5" />
              <span className="text-[10px] font-medium">{item.label}</span>
              {item.action === 'coach' && unreadBadge && <span className="absolute right-2 top-0">{unreadBadge}</span>}
            </Tag>
            );
          })}

          {/* More button with overflow menu */}
          <div className="relative" ref={moreMenuRef}>
            <button
              type="button"
              aria-expanded={showMoreMenu}
              aria-haspopup="menu"
              onClick={() => setShowMoreMenu(!showMoreMenu)}
              className={cn(
                "flex flex-col items-center gap-1 px-2 py-1.5 rounded-lg min-w-[60px] transition-colors",
                showMoreMenu || mobileMoreItems.some(item => isActive(item.path))
                  ? "text-accent-default"
                  : "text-text-tertiary hover:text-text-body"
              )}
            >
              <MoreHorizontal className="w-5 h-5" />
              <span className="text-[10px] font-medium">More</span>
            </button>

            {/* Overflow popup */}
            {showMoreMenu && (
              <div data-more-menu className="absolute bottom-full right-1 mb-2 w-52 max-h-[calc(100dvh-var(--mobile-nav-h)-var(--safe-top)-1rem)] overflow-y-auto overscroll-contain bg-bg-surface rounded-lg shadow-lg border border-border-default py-1 z-50">
                {mobileMoreItems.map((item, idx) => (
                  <React.Fragment key={item.path}>
                    {viewingAthlete && item.athleteData && (idx === 0 || !mobileMoreItems[idx - 1]?.athleteData) && (
                      <div className="px-4 py-1 text-[10px] font-semibold text-accent-default uppercase tracking-wider">
                        {viewingAthlete.username}'s data
                      </div>
                    )}
                    {item.dividerBefore && (
                      <div className="border-t border-border-default my-1" />
                    )}
                    <Link
                      to={item.path}
                      className={cn(
                        "flex items-center gap-3 px-4 py-2.5 text-sm font-medium transition-colors",
                        isActive(item.path)
                          ? "text-accent-default bg-accent-tint"
                          : viewingAthlete && item.athleteData
                            ? "bg-accent-tint text-accent-default"
                            : "text-text-secondary hover:bg-bg-raised"
                      )}
                    >
                      <item.icon className="w-4 h-4" />
                      <span>{item.label}</span>
                    </Link>
                  </React.Fragment>
                ))}
                <div className="border-t border-border-subtle my-1" />
                <button
                  onClick={logout}
                  className="flex items-center gap-3 px-4 py-2.5 text-sm font-medium w-full text-text-secondary hover:bg-bg-surface hover:text-status-danger transition-colors"
                >
                  <LogOut className="w-4 h-4" />
                  <span>Logout</span>
                </button>
              </div>
            )}
          </div>
        </div>
      </nav>

      {/* Mobile Overlay */}
      {isMobileOpen && (
        <div
          className="fixed inset-0 bg-black/50 z-30 md:hidden"
          onClick={() => setIsMobileOpen(false)}
        />
      )}
    </>
  );
};

export default Sidebar;